/**
 * useAdminSession - Backend-driven admin session check.
 *
 * Pattern follows useShopStatus/useSoloStatus — layout guard + backend check.
 * Caches result to avoid re-fetching on every route transition within /admin/*.
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@/app/providers/AuthProvider';
import { ADMIN_SESSION_STALE_MS, adminKeys } from './adminQueryKeys';
import { getAdminSession } from '../api/admin.api';
import type { AdminSession } from '../types/admin.types';

const getHttpStatus = (error: unknown): number | undefined => {
  const response = (error as { response?: { status?: unknown } })?.response;
  return typeof response?.status === 'number' ? response.status : undefined;
};

interface UseAdminSessionReturn {
  session: AdminSession | null;
  isLoading: boolean;
  isAdmin: boolean;
  error: boolean;
  isForbidden: boolean;
  isUnauthorized: boolean;
  statusCode?: number;
  refresh: () => Promise<void>;
}

export const useAdminSession = (): UseAdminSessionReturn => {
  const {
    accessToken,
    isAuthenticated,
    isLoading: isAuthLoading,
    refreshUser,
  } = useAuthContext();
  const queryEnabled = isAuthenticated && !!accessToken;
  const query = useQuery({
    queryKey: adminKeys.session(),
    queryFn: () => getAdminSession(accessToken ?? undefined),
    enabled: queryEnabled,
    staleTime: ADMIN_SESSION_STALE_MS,
    retry: (failureCount, error) => {
      const statusCode = getHttpStatus(error);
      if (statusCode === 401 || statusCode === 403) return false;
      return failureCount < 1;
    },
  });

  const refresh = useCallback(async () => {
    if (!accessToken) {
      await refreshUser();
      return;
    }
    await query.refetch();
  }, [accessToken, query, refreshUser]);

  const session = query.data ?? null;
  const missingAccessToken =
    isAuthenticated && !isAuthLoading && !accessToken;
  const statusCode = missingAccessToken ? 401 : getHttpStatus(query.error);
  const missingSession =
    queryEnabled && !query.isLoading && !query.isError && !session;
  const error = missingAccessToken || query.isError || missingSession;

  return {
    session,
    isLoading: isAuthLoading || (queryEnabled && query.isLoading),
    isAdmin: !!session?.isAdmin,
    error,
    isForbidden: statusCode === 403,
    isUnauthorized: statusCode === 401,
    statusCode,
    refresh,
  };
};
